// src/pages/Transactions/Transactions.js
/* global BigInt */
import React, { useContext, useState, useEffect } from 'react';
import { WalletContext } from '../../contexts/WalletContext';
import { NetworkContext } from '../../contexts/NetworkContext';
import { TransactionContext, TRANSACTION_TYPES } from '../../contexts/TransactionContext';
import { useTranslation } from 'react-i18next';
import { 
  FiExternalLink, 
  FiArrowUpRight, 
  FiArrowDownLeft, 
  FiRefreshCw, 
  FiSearch, 
  FiFilter, 
  FiX, 
  FiClock 
} from 'react-icons/fi';
import { 
  formatAddress, 
  getRelativeTime, 
  formatTime, 
  formatValue, 
  getTransactionTypeText
} from '../../utils/transactionUtils';
import { supportedChains } from '../../chains';
import useTransactionHistory from './hooks/useTransactionHistory';
import './Transactions.css';

const TRANSACTIONS_PER_PAGE = 30;

const Transactions = () => {
  const { t } = useTranslation();
  const { currentWallet, address } = useContext(WalletContext);
  const { currentChain } = useContext(NetworkContext);
  
  // Use the transaction history hook
  const { 
    transactions: displayTransactions,
    isLoading,
    isRefreshing,
    isLoadingMore,
    error,
    hasMore,
    refreshTransactions,
    loadMoreTransactions
  } = useTransactionHistory({ limit: TRANSACTIONS_PER_PAGE });
  
  // State variables
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter transactions when search term or active filters change
  useEffect(() => {
    if (!displayTransactions || displayTransactions.length === 0) {
      setFilteredTransactions([]);
      return;
    }
    
    let filtered = [...displayTransactions];
    
    // Apply type filters
    if (activeFilters.length > 0) {
      filtered = filtered.filter(tx => activeFilters.includes(tx.type));
    }
    
    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(tx => 
        (tx.hash && tx.hash.toLowerCase().includes(term)) || 
        (tx.from && tx.from.toLowerCase().includes(term)) || 
        (tx.to && tx.to.toLowerCase().includes(term)) ||
        (tx.tokenSymbol && tx.tokenSymbol.toLowerCase().includes(term))
      );
    }
    
    setFilteredTransactions(filtered);
  }, [displayTransactions, activeFilters, searchTerm]);
  
  // Handle search input
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Toggle a filter
  const toggleFilter = (filter) => {
    setActiveFilters(prev => 
      prev.includes(filter)
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };
  
  // Clear all filters
  const clearFilters = () => {
    setActiveFilters([]);
    setSearchTerm('');
  };
  
  // Determine transaction direction icon and CSS class
  const getTransactionIconAndClass = (tx, userAddress) => {
    let directionIcon, amountClass;
    
    if (tx.type === TRANSACTION_TYPES.SEND) {
      directionIcon = <FiArrowUpRight className="transaction-icon send" />;
      amountClass = "amount-sent";
    } else if (tx.type === TRANSACTION_TYPES.RECEIVE) {
      directionIcon = <FiArrowDownLeft className="transaction-icon receive" />;
      amountClass = "amount-received";
    } else {
      directionIcon = <FiClock className="transaction-icon" />;
      amountClass = "amount-neutral";
    }
    
    return { directionIcon, amountClass };
  };
  
  // Safe string conversion helpers
  const safeToString = (value) => typeof value === 'string' ? value : String(value || '');
  const safeToLowerCase = (value) => typeof value === 'string' ? value.toLowerCase() : '';
  
  // Get native token symbol for a chain
  const getNativeTokenSymbol = (chainKey) => {
    if (supportedChains[chainKey]) {
      return supportedChains[chainKey].nativeTokenSymbol;
    }
    // Fallback to uppercase network name if chain not found
    return chainKey.toUpperCase();
  };
  
  // Handle load more click
  const handleLoadMore = () => {
    console.log("Load more clicked, current state:", { isLoadingMore, hasMore });
    if (!isLoadingMore && hasMore) {
      loadMoreTransactions();
    }
  };
  
  // Render transaction card
  const renderTransactionCard = (tx) => {
    const { directionIcon, amountClass } = getTransactionIconAndClass(tx, address);
    const userAddressLower = safeToLowerCase(address);
    
    return (
      <div 
        className={`transaction-card ${tx.status !== 'success' ? 'failed' : ''}`} 
        key={tx.hash}
      >
        <div className="transaction-header">
          <div className="transaction-type-badge" data-type={tx.type.toLowerCase()}>
            {directionIcon}
            {getTransactionTypeText(tx.type, t)}
          </div>
          <div className="transaction-date">
            <div className="relative-time">{getRelativeTime(tx.timestamp, t)}</div>
            <div className="exact-time">{formatTime(tx.timestamp, t)}</div>
          </div>
        </div>
        
        <div className="transaction-details">
          <div className="detail-row">
            <span className="detail-label">{t('transactions.hash')}</span>
            <span className="detail-value hash">
              {formatAddress(safeToString(tx.hash), t)}
              <a 
                href={tx.explorerUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="explorer-link"
                aria-label={t('transactions.viewOnExplorer')}
              >
                <FiExternalLink />
              </a>
            </span>
          </div>
          
          <div className="detail-row">
            <span className="detail-label">{t('transactions.from')}</span>
            <span className={`detail-value address ${safeToLowerCase(tx.from) === userAddressLower ? 'self-address' : ''}`}>
              {formatAddress(safeToString(tx.from), t)}
            </span>
          </div>
          
          <div className="detail-row">
            <span className="detail-label">{t('transactions.to')}</span>
            <span className={`detail-value address ${tx.to && safeToLowerCase(tx.to) === userAddressLower ? 'self-address' : ''}`}>
              {formatAddress(safeToString(tx.to), t)}
            </span>
          </div>
          
          <div className="detail-row">
            <span className="detail-label">{t('transactions.amount')}</span>
            <span className={`detail-value ${amountClass}`}>
              {formatValue(tx.value, tx.tokenDecimal)} {tx.tokenSymbol}
            </span>
          </div>
          
          <div className="detail-row">
            <span className="detail-label">{t('transactions.fee')}</span>
            <span className="detail-value fee">
              {formatValue(tx.fee, 18)} {getNativeTokenSymbol(tx.network)}
            </span>
          </div>
          
          {tx.status !== 'success' && (
            <div className="transaction-error-badge">
              {t('transactions.failed')}
            </div>
          )}
        </div>
      </div>
    );
  };
  
  return (
    <div className="transactions-container">
      <div className="transactions-header">
        <h2>{t('transactions.title')}</h2>
        <button 
          className={`refresh-button ${isRefreshing ? 'refreshing' : ''}`}
          onClick={refreshTransactions}
          disabled={isLoading || isRefreshing}
        >
          <FiRefreshCw className={isRefreshing ? 'rotating' : ''} />
        </button>
      </div>
      
      {currentChain && address && (
        <div className="wallet-chain-info">
          <span>{formatAddress(address, t)}</span>
          <span className="chain-separator">•</span>
          <span>{currentChain.name}</span>
        </div>
      )}
      
      <div className="transactions-filters">
        <div className="search-container">
          <FiSearch className="search-icon" />
          <input 
            type="text"
            className="search-input"
            placeholder={t('transactions.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearch}
          />
          {searchTerm && (
            <button 
              className="clear-search-btn"
              onClick={() => setSearchTerm('')}
              aria-label={t('transactions.clearSearch')}
            >
              <FiX />
            </button>
          )}
        </div>
        
        <button 
          className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
          aria-label={t('transactions.toggleFilters')}
        >
          <FiFilter />
          {activeFilters.length > 0 && (
            <span className="filter-count">{activeFilters.length}</span>
          )}
        </button>
      </div>
      
      {showFilters && (
        <div className="filter-options">
          <div className="filter-buttons">
            {Object.values(TRANSACTION_TYPES).map(type => (
              <button
                key={type}
                className={`filter-btn ${activeFilters.includes(type) ? 'active' : ''}`}
                onClick={() => toggleFilter(type)}
              >
                {getTransactionTypeText(type, t)}
              </button>
            ))}
          </div>
          
          {activeFilters.length > 0 && (
            <button className="clear-filters-btn" onClick={clearFilters}>
              {t('transactions.clearFilters')}
            </button>
          )}
        </div>
      )}
      
      <div className="transactions-list">
        {isLoading && !isRefreshing && displayTransactions.length === 0 ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>{t('transactions.loading')}</p>
          </div>
        ) : error && displayTransactions.length === 0 ? (
          <div className="error-state">
            <p>{t('transactions.error')}</p>
            <p className="error-details">{error}</p>
            <button className="retry-btn" onClick={refreshTransactions}>
              {t('transactions.retry')}
            </button>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="empty-state">
            {displayTransactions.length === 0 ? (
              <>
                <p>{t('transactions.noTransactionsYet')}</p>
                <p className="suggestion-text">
                  {t('transactions.suggestion')}
                </p>
              </>
            ) : (
              <>
                <p>{t('transactions.noMatchingTransactions')}</p>
                <button 
                  className="clear-filters-btn"
                  onClick={clearFilters}
                >
                  {t('transactions.clearFilters')}
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            {/* Transaction list */}
            {filteredTransactions.map((tx) => renderTransactionCard(tx))}
            
            {/* Load More button */}
            {hasMore && (
              <div className="load-more-container">
                <button 
                  className="load-more-btn"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? (
                    <>
                      <div className="loading-spinner small"></div>
                      <span>{t('transactions.loadingMore')}</span>
                    </>
                  ) : (
                    t('transactions.loadMore')
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Transactions;